<template>
  <div class="database-table-creation-page">
    <el-card class="creation-card" shadow="never">
      <div slot="header" class="creation-card__header">
        <div>
          <h2 class="creation-card__title">数据库表创建</h2>
          <p class="creation-card__subtitle">填写表名、字段名和字段中文注释后，后端会在 MySQL 中创建一张新表。</p>
        </div>
        <el-button plain icon="el-icon-refresh" @click="resetForm">重置</el-button>
      </div>

      <el-form label-width="110px" class="table-form">
        <el-form-item label="表名" required>
          <el-input
            v-model.trim="tableForm.tableName"
            clearable
            placeholder="例如：daily_record"
          />
          <div class="form-tip">表名必须以字母开头，只能包含字母、数字和下划线。</div>
        </el-form-item>

        <el-form-item label="字段列表" required>
          <el-table :data="tableForm.fields" border class="field-table">
            <el-table-column label="字段名" min-width="180">
              <template slot-scope="scope">
                <el-input
                  v-model.trim="scope.row.fieldName"
                  placeholder="例如：title"
                />
              </template>
            </el-table-column>

            <el-table-column label="中文注释" min-width="220">
              <template slot-scope="scope">
                <el-input
                  v-model.trim="scope.row.fieldComment"
                  placeholder="例如：标题"
                />
              </template>
            </el-table-column>

            <el-table-column label="字段类型" width="170">
              <template slot-scope="scope">
                <el-select v-model="scope.row.fieldType" placeholder="字段类型">
                  <el-option
                    v-for="fieldTypeItem in fieldTypeOptions"
                    :key="fieldTypeItem.value"
                    :label="fieldTypeItem.label"
                    :value="fieldTypeItem.value"
                  />
                </el-select>
              </template>
            </el-table-column>

            <el-table-column label="操作" width="120" align="center">
              <template slot-scope="scope">
                <el-button
                  size="mini"
                  type="danger"
                  plain
                  icon="el-icon-delete"
                  :disabled="tableForm.fields.length === 1"
                  @click="removeFieldRow(scope.$index)"
                >
                  删除
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <el-button
            class="add-field-button"
            type="primary"
            plain
            icon="el-icon-plus"
            @click="addFieldRow"
          >
            添加字段
          </el-button>
        </el-form-item>

        <el-form-item>
          <el-button
            type="primary"
            icon="el-icon-check"
            :loading="isSubmitting"
            @click="createDatabaseTable"
          >
            创建表
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card v-if="createResult" class="result-card" shadow="never">
      <div slot="header" class="result-card__header">
        <span>创建结果</span>
      </div>
      <el-descriptions :column="1" border>
        <el-descriptions-item label="表名">{{ createResult.tableName }}</el-descriptions-item>
        <el-descriptions-item label="字段数量">{{ createResult.fields.length }}</el-descriptions-item>
      </el-descriptions>
      <el-table :data="createResult.fields" border class="result-table">
        <el-table-column prop="fieldName" label="字段名" />
        <el-table-column prop="fieldComment" label="中文注释" />
        <el-table-column prop="fieldType" label="字段类型" />
      </el-table>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'DatabaseTableCreation',
  data() {
    return {
      /**
       * 当前数据库建表表单对象。
       * tableName 是要创建的数据库表名，fields 是用户填写的业务字段列表。
       * @type {{tableName: string, fields: Array<{fieldName: string, fieldComment: string, fieldType: string}>}}
       */
      tableForm: {
        tableName: '',
        fields: this.createDefaultFieldList()
      },
      /**
       * 当前前端允许选择的字段类型列表。
       * 后端也会做同样的白名单校验，避免传入不受控的 SQL 类型。
       * @type {Array<{label: string, value: string}>}
       */
      fieldTypeOptions: [
        { label: '短文本 VARCHAR(255)', value: 'VARCHAR(255)' },
        { label: '长文本 TEXT', value: 'TEXT' },
        { label: '整数 INT', value: 'INT' },
        { label: '大整数 BIGINT', value: 'BIGINT' },
        { label: '金额 DECIMAL(10,2)', value: 'DECIMAL(10,2)' },
        { label: '日期时间 DATETIME', value: 'DATETIME' },
        { label: '日期 DATE', value: 'DATE' },
        { label: '开关 TINYINT(1)', value: 'TINYINT(1)' }
      ],
      /**
       * 当前是否正在提交建表请求。
       * @type {boolean}
       */
      isSubmitting: false,
      /**
       * 当前最近一次建表成功后后端返回的结果。
       * @type {{tableName: string, fields: Array<{fieldName: string, fieldComment: string, fieldType: string}>} | null}
       */
      createResult: null,
      /**
       * 当前表名和字段名的校验正则。
       * 命名必须以字母开头，只能包含字母、数字和下划线。
       * @type {RegExp}
       */
      identifierPattern: /^[A-Za-z][A-Za-z0-9_]{0,63}$/
    }
  },
  methods: {
    /**
     * 创建默认字段列表。
     * 页面初始化和重置时都会使用这份默认结构。
     * @returns {Array<{fieldName: string, fieldComment: string, fieldType: string}>}
     */
    createDefaultFieldList() {
      return [
        {
          /**
           * 当前字段的英文数据库字段名。
           */
          fieldName: '',
          /**
           * 当前字段在数据库中的中文注释。
           */
          fieldComment: '',
          /**
           * 当前字段的数据库类型，默认按短文本创建。
           */
          fieldType: 'VARCHAR(255)'
        }
      ]
    },
    /**
     * 新增一行字段输入。
     */
    addFieldRow() {
      this.tableForm.fields.push({
        /**
         * 当前新增字段的英文数据库字段名。
         */
        fieldName: '',
        /**
         * 当前新增字段的中文注释。
         */
        fieldComment: '',
        /**
         * 当前新增字段的数据库类型。
         */
        fieldType: 'VARCHAR(255)'
      })
    },
    /**
     * 删除指定下标的字段行。
     * @param {number} fieldIndex 需要删除的字段行下标。
     */
    removeFieldRow(fieldIndex) {
      if (this.tableForm.fields.length === 1) {
        return
      }

      this.tableForm.fields.splice(fieldIndex, 1)
    },
    /**
     * 重置建表表单。
     */
    resetForm() {
      this.tableForm = {
        tableName: '',
        fields: this.createDefaultFieldList()
      }
      this.createResult = null
    },
    /**
     * 校验表名是否符合数据库命名规则。
     * @returns {boolean}
     */
    validateTableName() {
      if (!this.identifierPattern.test(this.tableForm.tableName)) {
        this.$toast.warning('表名必须以字母开头，并且只能包含字母、数字和下划线')
        return false
      }

      return true
    },
    /**
     * 校验字段列表是否填写完整且没有重复字段名。
     * @returns {boolean}
     */
    validateFields() {
      /**
       * 当前用于检测重复字段名的集合。
       * @type {Set<string>}
       */
      const fieldNameSet = new Set()

      for (const fieldItem of this.tableForm.fields) {
        if (!this.identifierPattern.test(fieldItem.fieldName)) {
          this.$toast.warning('字段名必须以字母开头，并且只能包含字母、数字和下划线')
          return false
        }

        if (fieldItem.fieldName.toLowerCase() === 'id') {
          this.$toast.warning('id 会自动作为主键创建，请不要手动添加 id 字段')
          return false
        }

        if (!fieldItem.fieldComment) {
          this.$toast.warning(`字段 ${fieldItem.fieldName} 的中文注释不能为空`)
          return false
        }

        if (fieldNameSet.has(fieldItem.fieldName.toLowerCase())) {
          this.$toast.warning(`字段 ${fieldItem.fieldName} 重复，请修改后再提交`)
          return false
        }

        fieldNameSet.add(fieldItem.fieldName.toLowerCase())
      }

      return true
    },
    /**
     * 构建提交给后端的字段列表。
     * @returns {Array<{fieldName: string, fieldComment: string, fieldType: string}>}
     */
    buildPayloadFields() {
      return this.tableForm.fields.map(fieldItem => ({
        /**
         * 当前字段的英文数据库字段名。
         */
        fieldName: fieldItem.fieldName,
        /**
         * 当前字段的中文注释。
         */
        fieldComment: fieldItem.fieldComment,
        /**
         * 当前字段的数据库类型。
         */
        fieldType: fieldItem.fieldType
      }))
    },
    /**
     * 提交建表请求到后端。
     */
    async createDatabaseTable() {
      if (!this.validateTableName() || !this.validateFields()) {
        return
      }

      this.isSubmitting = true

      try {
        /**
         * 当前提交给后端的建表参数。
         * @type {{tableName: string, fields: Array<{fieldName: string, fieldComment: string, fieldType: string}>}}
         */
        const requestPayload = {
          tableName: this.tableForm.tableName,
          fields: this.buildPayloadFields()
        }
        /**
         * 当前后端建表接口返回的响应对象。
         */
        const response = await this.$axios.post('/api/database/tables', requestPayload)

        if (response.data.code !== 200) {
          this.$toast.error(response.data.message || '数据库表创建失败')
          return
        }

        this.createResult = response.data.data
        this.$toast.success('数据库表创建成功')
      } catch (error) {
        /**
         * 当前后端返回的错误响应体。
         */
        const errorResponse = error.response && error.response.data
        /**
         * 当前后端返回的 HTTP 状态码。
         */
        const errorStatusCode = error.response && error.response.status

        if (errorStatusCode === 404) {
          this.$toast.error('后端建表接口不存在，请确认已部署 backend/routes/database.js 并重启后端服务')
          return
        }

        this.$toast.error((errorResponse && errorResponse.message) || '数据库表创建失败')
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.database-table-creation-page {
  /* 撑满内容区，滚动下沉到模块内部 */
  height: 100%;
  box-sizing: border-box;
  overflow: auto;
  padding: 0;
}

.creation-card,
.result-card {
  border-radius: 8px;
}

.result-card {
  margin-top: 20px;
}

.creation-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.creation-card__title {
  margin: 0;
  color: #303133;
  font-size: 22px;
}

.creation-card__subtitle {
  margin: 8px 0 0;
  color: #606266;
  font-size: 14px;
}

.table-form {
  max-width: 980px;
}

.form-tip {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}

.field-table {
  width: 100%;
}

.add-field-button {
  margin-top: 14px;
}

.result-card__header {
  color: #303133;
  font-weight: 600;
}

.result-table {
  margin-top: 16px;
}
</style>
